import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import { formatSellingUnit, formatUnitConversion } from './admin-products/catalogUnits';

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();

  const handleQuantityChange = (item, value) => {
    // Allow empty input for editing
    if (value === '') {
      return;
    }
    
    const minOrder = item.min_order_quantity || 1;
    const quantity = parseFloat(value);
    
    if (!isNaN(quantity) && quantity > 0) {
      if (quantity < minOrder) {
        toast.error(`MOQ for ${item.name} is ${minOrder} sale units`);
        updateQuantity(item.id, minOrder);
      } else {
        updateQuantity(item.id, quantity);
      }
    } else if (quantity === 0) {
      // Remove item if quantity is 0
      removeFromCart(item.id);
    }
  };

  const handleQuantityBlur = (item, value) => {
    const minOrder = item.min_order_quantity || 1;
    // If input is empty or invalid on blur, set to minimum
    if (value === '' || parseFloat(value) < minOrder) {
      updateQuantity(item.id, minOrder);
    }
  };

  const handleIncrement = (item) => {
    updateQuantity(item.id, item.quantity + 1);
  };

  const handleDecrement = (item) => {
    const minOrder = item.min_order_quantity || 1;
    const newQty = item.quantity - 1;
    if (newQty < minOrder) {
      toast.error(`MOQ for ${item.name} is ${minOrder} sale units`);
      return;
    }
    updateQuantity(item.id, newQty);
  };

  if (cart.length === 0) {
    return (
      <div className="cart-page min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <ShoppingBag className="h-24 w-24 text-gray-300 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
          <p className="text-gray-600 mb-8">Add some products to get started!</p>
          <Link to="/shop">
            <Button size="lg">Browse Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Shopping Cart</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {cart.map((item) => (
                <Card key={item.id} className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex gap-6">
                      {/* Product Image */}
                      <div className="h-32 w-32 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=No+Image'; }}
                        />
                      </div>

                      {/* Product Info */}
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <Link to={`/product/${item.id}`}>
                              <h3 className="text-lg font-bold text-gray-900 hover:text-green-600 transition-colors">
                                {item.name}
                              </h3>
                            </Link>
                            <p className="text-gray-600 text-sm" data-testid={`cart-unit-price-${item.id}`}>
                              ₦{item.price.toLocaleString()} per {formatSellingUnit(item.unit_value, item.unit)}
                            </p>
                            <p className="text-gray-500 text-xs">{formatUnitConversion(item.unit_value, item.unit)}</p>
                            {item.min_order_quantity > 1 && (
                              <p className="text-orange-600 text-xs mt-1">
                                MOQ: {item.min_order_quantity} sale units
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-500 hover:text-red-700 transition-colors p-2"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                          {/* Quantity Controls */}
                          <div className="w-full sm:w-auto">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Quantity (sale units)
                            </label>
                            <div className="flex items-center gap-3">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-10 w-10"
                                onClick={() => handleDecrement(item)}
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              
                              <Input
                                type="number"
                                min={item.min_order_quantity || 1}
                                step="0.1"
                                value={item.quantity}
                                onChange={(e) => handleQuantityChange(item, e.target.value)}
                                onBlur={(e) => handleQuantityBlur(item, e.target.value)}
                                className="w-24 text-center text-lg font-semibold"
                                data-testid={`cart-quantity-input-${item.id}`}
                              />
                              
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-10 w-10"
                                onClick={() => handleIncrement(item)}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Each sale unit is {formatSellingUnit(item.unit_value, item.unit)}.
                            </p>
                          </div>

                          {/* Price */}
                          <div className="text-right w-full sm:w-auto">
                            <p className="text-sm text-gray-600 mb-1">Total Price</p>
                            <div className="text-3xl font-bold text-green-600">
                              ₦{(item.price * item.quantity).toLocaleString(undefined, { 
                                minimumFractionDigits: 2, 
                                maximumFractionDigits: 2 
                              })}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {item.quantity} × {formatSellingUnit(item.unit_value, item.unit)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Clear Cart */}
            <div className="mt-6">
              <Button
                variant="outline"
                onClick={clearCart}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear Cart
              </Button>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 border-2 border-green-100">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>

                <div className="space-y-4 mb-6">
                  {/* Individual Items */}
                  {cart.map((item) => (
                    <div key={item.id} className="pb-3 border-b">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-700 font-medium">{item.name}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>{item.quantity} × {formatSellingUnit(item.unit_value, item.unit)}</span>
                        <span className="font-semibold text-gray-900">
                          ₦{(item.price * item.quantity).toLocaleString(undefined, { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                  
                  <div className="pt-4 space-y-2">
                    <div className="flex justify-between text-gray-700">
                      <span>Total Items</span>
                      <span className="font-semibold">
                        {cart.reduce((sum, item) => sum + item.quantity, 0).toLocaleString()} sale units
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>Subtotal</span>
                      <span className="font-semibold">
                        ₦{getCartTotal().toLocaleString(undefined, { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })}
                      </span>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between text-lg font-bold text-gray-900">
                      <span>Total</span>
                      <span className="text-green-600">
                        ₦{getCartTotal().toLocaleString(undefined, { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      *Delivery fee will be calculated at checkout
                    </p>
                  </div>
                </div>

                <Link to="/checkout">
                  <Button size="lg" className="w-full mb-4">
                    Proceed to Checkout
                  </Button>
                </Link>

                <Link to="/shop">
                  <Button size="lg" variant="outline" className="w-full">
                    Continue Shopping
                  </Button>
                </Link>

                {/* Bulk Order Info */}
                <div className="mt-6 p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800 font-medium mb-2">
                    💡 Bulk Orders Welcome!
                  </p>
                  <p className="text-xs text-green-700">
                    Need large quantities? Each sale unit clearly shows its conversion, including tonnes and kilograms.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;